import moment from 'moment-timezone'

export const formatDate = (date, format = 'MM/DD/YYYY') => {
  return moment(date).format(format)
}

export const formateMonthDate = (date) => {
  return moment.utc(date).format('MMMM Do')
}

// Date-only values (e.g. a payment due date stored at midnight UTC) are read in
// UTC so they never shift a day back for viewers behind UTC.
export const formatFullDate = (date) => {
  return moment.utc(date).format('MMMM D, YYYY')
}

export const formateAmPm = (time) => {
  return moment(time, 'HH:mm')
    .format('h:mm a')
    .replace('am', 'a.m.')
    .replace('pm', 'p.m.')
}

export const insightDateFormat = (date) => {
  return moment(date).format('MMM Do YYYY')
}

export const getAssessmentCurrentData = (date, dateFormat) => {
  if (date) {
    return moment(date).format(dateFormat ? dateFormat : 'MMM Do, YYYY')
  } else {
    return moment().format(dateFormat ? dateFormat : 'MMM Do, YYYY')
  }
}

export const sortedByDate = (data, key) => {
  return data?.sort((a, b) => (moment(a?.[key]) > moment(b?.[key]) ? -1 : 1))
}

export const formatDuration = (duration) => {
  return moment.duration(duration, 'seconds').format('mm:ss', { trim: false })
}

/**
 * Resolve start/end from the first programDatesList entry (index 0).
 * Falls back to legacy startDate/endDate on the quest.
 */
export const getProgramDatesRange = (quest) => {
  const firstDate = quest?.programDatesList?.[0]
  if (firstDate?.startDate || firstDate?.endDate) {
    return {
      start: firstDate.startDate || null,
      end: firstDate.endDate || firstDate.startDate || null,
    }
  }
  return {
    start: quest?.startDate || null,
    end: quest?.endDate || null,
  }
}

export const sortProgramDatesList = (datesList) => {
  if (!Array.isArray(datesList)) return []

  return [...datesList].sort(
    (a, b) =>
      moment.utc(a?.startDate).valueOf() - moment.utc(b?.startDate).valueOf(),
  )
}

/**
 * Format a program date entry for Prosperity Dates section.
 * Examples:
 * - May 7, 2026 (Bhutan Experience)
 * - April 21-29, 2026 (Bhutan Experience)
 * - April 29 - May 7, 2026 (Bhutan Experience)
 * - December 27 - January 5, 2027 (Bhutan Experience)
 */
export const formatProgramDateEntry = (dateItem) => {
  if (!dateItem?.startDate) return ''

  const start = moment.utc(dateItem.startDate)
  const description = dateItem.description ? ` (${dateItem.description})` : ''

  if (!dateItem.endDate) {
    return `${start.format('MMMM D, YYYY')}${description}`
  }

  const end = moment.utc(dateItem.endDate)

  let datePart = ''
  if (start.year() === end.year()) {
    if (start.month() === end.month()) {
      if (start.date() === end.date()) {
        datePart = `${start.format('MMMM D, YYYY')}`
      } else {
        datePart = `${start.format('MMMM D')}-${end.format('D, YYYY')}`
      }
    } else {
      datePart = `${start.format('MMMM D')} - ${end.format('MMMM D, YYYY')}`
    }
  } else {
    // Cross-year: show year only once, after the ending date
    datePart = `${start.format('MMMM D')} - ${end.format('MMMM D, YYYY')}`
  }

  return `${datePart}${description}`
}

export const getFormattedDateRange = (startDateStr, endDateStr) => {
  if (!startDateStr) return ''

  if (!endDateStr) {
    return moment.utc(startDateStr).format('MMMM D, YYYY')
  }

  const start = moment.utc(startDateStr)
  const end = moment.utc(endDateStr)

  if (start.year() === end.year()) {
    if (start.month() === end.month()) {
      if (start.date() === end.date()) {
        return `${start.format('MMMM D, YYYY')}`
      }
      return `${start.format('MMMM D')}-${end.format('D, YYYY')}`
    }
    return `${start.format('MMMM D')} - ${end.format('MMMM D, YYYY')}`
  }

  // Cross-year: show year only once, after the ending date
  return `${start.format('MMMM D')} - ${end.format('MMMM D, YYYY')}`
}

export const getFormattedDateRangeForHeader = (startDateStr, endDateStr) => {
  if (!startDateStr || !endDateStr) return ''

  const start = moment.utc(startDateStr)
  const end = moment.utc(endDateStr)

  if (start.year() === end.year()) {
    if (start.month() === end.month()) {
      // Same month, same year
      return `${start.format('MMM D')} - ${end.format('D')} '${start.format(
        'YY',
      )}`
    } else {
      // Different month, same year - show days as well
      return `${start.format('MMM D')} - ${end.format('MMM D')} '${start.format(
        'YY',
      )}`
    }
  } else {
    // Different year
    return `${start.format('MMM D, YY')} - ${end.format('MMM D, YY')}`
  }
}

export const formatDateShort = (date) => {
  return moment(date).format('MM/DD/YY')
}

export const formatDayName = (date) => {
  return moment(date).format('dddd')
}

export const getCurrentMoment = () => {
  return moment()
}

export const isValidMomentDate = (date) => {
  return moment(date).isValid()
}
